import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { registerSW } from "./lib/sw-register";
import InstallPrompt from "./components/InstallPrompt";
import OfflineIndicator from "./components/OfflineIndicator";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProfilePage from "./pages/ProfilePage";
import AppLayout from "./components/AppLayout";
import DashboardPage from "./pages/DashboardPage";
import MaterialsPage from "./pages/MaterialsPage";
import EntriesPage from "./pages/EntriesPage";
import TechnicianStockPage from "./pages/TechnicianStockPage";
import FleetPage from "./pages/FleetPage";
import WorkOrdersPage from "./pages/WorkOrdersPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import UsersPage from "./pages/UsersPage";
import EquipmentPage from "./pages/EquipmentPage";
import CompanyStockPage from "./pages/CompanyStockPage";
import ToolsPage from "./pages/ToolsPage";
import TransfersPage from "./pages/TransfersPage";
import OperatorsPage from "./pages/OperatorsPage";

// Role constants (extracted so <ProtectedRoute roles={...}> receives a stable reference)
const ROLES_ADMIN_ONLY = ["admin"];
const ROLES_TECNICO_ONLY = ["tecnico"];
const ROLES_ADMIN_GESTOR = ["admin", "gestor"];
const ROLES_WAREHOUSE = ["admin", "gestor", "almacenero"]; // Storekeeper-eligible pages

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
      <div className="label-caps" data-testid="loading">Carregando...</div>
    </div>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <LoadingScreen />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/perfil" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/materiais" element={<ProtectedRoute roles={ROLES_WAREHOUSE}><MaterialsPage /></ProtectedRoute>} />
      <Route path="/equipamentos" element={<ProtectedRoute roles={ROLES_WAREHOUSE}><EquipmentPage /></ProtectedRoute>} />
      <Route path="/estoque-empresa" element={<ProtectedRoute roles={ROLES_WAREHOUSE}><CompanyStockPage /></ProtectedRoute>} />
      <Route path="/entradas" element={<ProtectedRoute roles={ROLES_WAREHOUSE}><EntriesPage /></ProtectedRoute>} />
      <Route path="/estoque-tecnicos" element={<ProtectedRoute roles={ROLES_WAREHOUSE}><TechnicianStockPage /></ProtectedRoute>} />
      <Route path="/meu-estoque" element={<ProtectedRoute roles={ROLES_TECNICO_ONLY}><TechnicianStockPage selfOnly /></ProtectedRoute>} />
      <Route path="/frota" element={<ProtectedRoute roles={ROLES_ADMIN_GESTOR}><FleetPage /></ProtectedRoute>} />
      <Route path="/ferramentas" element={<ProtectedRoute roles={ROLES_ADMIN_GESTOR}><ToolsPage /></ProtectedRoute>} />
      <Route path="/minhas-ferramentas" element={<ProtectedRoute roles={ROLES_TECNICO_ONLY}><ToolsPage selfOnly /></ProtectedRoute>} />
      <Route path="/transferencias" element={<ProtectedRoute><TransfersPage /></ProtectedRoute>} />
      <Route path="/ots" element={<ProtectedRoute><WorkOrdersPage /></ProtectedRoute>} />
      <Route path="/relatorios" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/config" element={<ProtectedRoute roles={ROLES_ADMIN_GESTOR}><SettingsPage /></ProtectedRoute>} />
      <Route path="/usuarios" element={<ProtectedRoute roles={ROLES_ADMIN_ONLY}><UsersPage /></ProtectedRoute>} />
      <Route path="/operadoras" element={<ProtectedRoute roles={ROLES_ADMIN_ONLY}><OperatorsPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  registerSW();
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <InstallPrompt />
          <OfflineIndicator />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
